import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { GoogleOAuthProvider } from '@react-oauth/google';
import axios from 'axios';
import { AuthProvider, useAuth } from './context/AuthContext';
import AuthCallback from './components/AuthCallback';
import Layout from './components/Layout';
import { canAccess, homeFor } from './lib/navConfig';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import IntegrationHealthPage from './pages/IntegrationHealthPage';
import DataIntegrityPage from './pages/DataIntegrityPage';
import PreSalesDashboard from './pages/PreSalesDashboard';
import SalesDashboard from './pages/SalesDashboard';
import LeadsPage from './pages/LeadsPage';
import TrialSlotsPage from './pages/TrialSlotsPage';
import PricingPage from './pages/PricingPage';
import PaymentsPage from './pages/PaymentsPage';
import UsersPage from './pages/UsersPage';
import SettingsPage from './pages/SettingsPage';
import MarketingDashboard from './pages/MarketingDashboard';
import DoctorsPage from './pages/DoctorsPage';
import CommissionsPage from './pages/CommissionsPage';
import AuditLogPage from './pages/AuditLogPage';
import ExportPage from './pages/ExportPage';
import TrialCheckoutPage from './pages/TrialCheckoutPage';
import TrialThankYouPage from './pages/TrialThankYouPage';
import GarbhsanskarFullPage, { GarbhsanskarFullThankYouPage } from './pages/GarbhsanskarFullPage';
import PublicCalendarPage from './pages/PublicCalendarPage';
import CalendarPage from './pages/CalendarPage';

import MarketingIntelPage from './pages/MarketingIntelPage';
import BlogPostsPage from './pages/blog/BlogPostsPage';
import BlogPostEditor from './pages/blog/BlogPostEditor';
import BlogAuthorsPage from './pages/blog/BlogAuthorsPage';
import BlogConfigPage from './pages/blog/BlogConfigPage';
import LmsIntegrationPage from './pages/LmsIntegrationPage';
import PayrollPage from './pages/PayrollPage';
import './App.css';

// Helper: read a cookie value by name (for CSRF double-submit pattern).
const _readCookie = (name) => {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  } catch (e) {
    return '';
  }
};

// Setup axios interceptor to add auth token + CSRF token to all requests.
// This runs on EVERY request so the tokens stay fresh across tabs.
axios.interceptors.request.use((config) => {
  // Only include credentials for API calls that need auth (not public endpoints)
  const isPublicEndpoint = config.url?.includes('/public/') || 
                           config.url?.includes('/book-consultation') ||
                           config.url?.includes('/trial/') ||
                           config.url?.includes('/book/');
  
  if (!isPublicEndpoint) {
    config.withCredentials = true;
    
    // Always read fresh token from localStorage (important for mobile after login)
    try {
      const token = localStorage.getItem('session_token');
      if (token && token.length > 10) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      // localStorage might not be available in some contexts
    }

    // Attach CSRF header for write methods. Backend compares against the
    // `csrf_token` cookie (double-submit pattern). Read lives are exempt.
    const method = (config.method || 'get').toUpperCase();
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      const csrf = _readCookie('csrf_token') || localStorage.getItem('csrf_token') || '';
      if (csrf) {
        config.headers['X-CSRF-Token'] = csrf;
      }
    }
  }
  
  return config;
});

// Smart Dashboard: each role lands on its ONE dashboard (single attribution rule per role).
const SmartDashboard = () => {
  const { user } = useAuth();

  if (user?.role === 'pre_sales') return <Navigate to="/presales-dashboard" replace />;
  if (user?.role === 'sales') return <Navigate to="/sales-dashboard" replace />;
  if (user?.role === 'marketing') return <Navigate to="/marketing" replace />;

  // Admin / finance / operations → company-wide Overall Dashboard
  return <Dashboard />;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // If user data was passed from AuthCallback, use it
  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Role gate for authenticated routes — same table as the sidebar (lib/navConfig).
const RoleRoute = ({ path, children }) => {
  const { user } = useAuth();
  if (user && !canAccess(path, user.role)) {
    return <Navigate to={homeFor(user.role)} replace />;
  }
  return children;
};

const AppRouter = () => {
  const location = useLocation();
  const { user } = useAuth();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public trial routes - no auth required */}
      <Route path="/trial/:referralCode" element={<TrialCheckoutPage />} />
      <Route path="/trial-thank-you" element={<TrialThankYouPage />} />
      <Route path="/garbhsanskar-full" element={<GarbhsanskarFullPage />} />
      <Route path="/garbhsanskar-full/thank-you" element={<GarbhsanskarFullThankYouPage />} />
      <Route path="/book-consultation" element={<PublicCalendarPage />} />
      <Route path="/book/:slug" element={<PublicCalendarPage />} />
      
      <Route path="/" element={<PublicRoute />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<SmartDashboard />} />
        <Route path="/presales-dashboard" element={<RoleRoute path="/presales-dashboard"><PreSalesDashboard /></RoleRoute>} />
        <Route path="/sales-dashboard" element={<RoleRoute path="/sales-dashboard"><SalesDashboard /></RoleRoute>} />
        <Route path="/leads" element={<RoleRoute path="/leads"><LeadsPage /></RoleRoute>} />
        <Route path="/trial-slots" element={<RoleRoute path="/trial-slots"><TrialSlotsPage /></RoleRoute>} />
        <Route path="/pricing" element={<RoleRoute path="/pricing"><PricingPage /></RoleRoute>} />
        <Route path="/payments" element={<RoleRoute path="/payments"><PaymentsPage /></RoleRoute>} />
        <Route path="/users" element={<RoleRoute path="/users"><UsersPage /></RoleRoute>} />
        <Route path="/settings" element={<RoleRoute path="/settings"><SettingsPage /></RoleRoute>} />
        <Route path="/marketing" element={<MarketingDashboard currentUser={user} />} />
        <Route path="/doctors" element={<RoleRoute path="/doctors"><DoctorsPage /></RoleRoute>} />
        <Route path="/commissions" element={<RoleRoute path="/commissions"><CommissionsPage /></RoleRoute>} />
        <Route path="/audit-log" element={<RoleRoute path="/audit-log"><AuditLogPage /></RoleRoute>} />
        <Route path="/export" element={<RoleRoute path="/export"><ExportPage /></RoleRoute>} />
        <Route path="/calendar" element={<RoleRoute path="/calendar"><CalendarPage /></RoleRoute>} />
        {/* /offers merged into /pricing → Discounts tab (V11). Keep redirect for old bookmarks. */}
        <Route path="/offers" element={<Navigate to="/pricing" replace />} />
        <Route path="/marketing-intel" element={<RoleRoute path="/marketing-intel"><MarketingIntelPage /></RoleRoute>} />
        <Route path="/blog/posts" element={<RoleRoute path="/blog/posts"><BlogPostsPage /></RoleRoute>} />
        <Route path="/blog/posts/:slug" element={<RoleRoute path="/blog/posts/:slug"><BlogPostEditor /></RoleRoute>} />
        <Route path="/blog/authors" element={<RoleRoute path="/blog/authors"><BlogAuthorsPage /></RoleRoute>} />
        <Route path="/blog/config" element={<RoleRoute path="/blog/config"><BlogConfigPage /></RoleRoute>} />
        <Route path="/lms-integration" element={<RoleRoute path="/lms-integration"><LmsIntegrationPage /></RoleRoute>} />
        <Route path="/payslips" element={<PayrollPage currentUser={user} />} />
        <Route path="/integration-health" element={<RoleRoute path="/integration-health"><IntegrationHealthPage /></RoleRoute>} />
        <Route path="/data-integrity" element={<RoleRoute path="/data-integrity"><DataIntegrityPage /></RoleRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const PublicRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <LoginPage />;
};

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster 
            position="top-right" 
            offset={72}
            richColors 
            closeButton
            toastOptions={{
              style: {
                fontFamily: 'Inter, sans-serif'
              }
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
